import { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import UIkit from "uikit";
import moment from "moment";

import { AppContexts } from "../../providers";
import FEATURES from "../../config/features";
import Intercom from "@intercom/messenger-js-sdk";
import { set } from "react-hook-form";

const Dashboard = () => {
  const { user, setUser } = useContext(AppContexts.AuthContext);
  const { get: getRental, rentals } = useContext(AppContexts.RentalsContext);
  const today = new Date();
  const formatDate = (date) => date.toISOString().split("T")[0];

  const [selectedFilter, setSelectedFilter] = useState("last7days");
  const [startDate, setStartDate] = useState(formatDate(new Date(today.setDate(today.getDate() - 7))));
  const [endDate, setEndDate] = useState(formatDate(new Date())); 

  const validateDates = () => {
    if (endDate && startDate && endDate < startDate) {
      alert("End date cannot be before start date.");
    }
  };

  const {
    emailDNSCheck,
    dnsErrors,
    dashboardCountsGuestsArrivingToday,
    dashboardCountsGuestsCurrentlyStaying,
    dashboardCountsGuestsDepartingToday,
    dashboardCountsGuestsLast30Days,
    dashboardCountsHomeGuides,
    dashboardCountsAreaGuides,
    dashboardCountsScheduledMessages,
    dashboardCountsContacts,
    dashboardCountsTextMessagesSent,
    dashboardCountsGuestGuideViews,
    dashboardCountsAvgGuestRating,
    dashboardCountsSurveyResponses,
    dashboardCountsExtendStaysMessagesSent,
    dashboardCountsLeadsGenerated,
    dashboardCountsOrdersReceived,
    dashboardCountsWorkOrdersCreated
  } = useContext(AppContexts.UtilContext);

  // 16 loading states
  const [loadingArrivingToday, setLoadingArrivingToday] = useState(true);
  const [loadingCurrentlyStaying, setLoadingCurrentlyStaying] = useState(true);
  const [loadingDepartingToday, setLoadingDepartingToday] = useState(true);
  const [loadingLast30Days, setLoadingLast30Days] = useState(true);
  const [loadingHomeGuides, setLoadingHomeGuides] = useState(true);
  const [loadingAreaGuides, setLoadingAreaGuides] = useState(true);
  const [loadingScheduledMessages, setLoadingScheduledMessages] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [loadingTextMessagesSent, setLoadingTextMessagesSent] = useState(true);
  const [loadingGuestGuideViews, setLoadingGuestGuideViews] = useState(true);
  const [loadingAverageGuestRating, setLoadingAverageGuestRating] = useState(true);
  const [loadingSurveyResponses, setLoadingSurveyResponses] = useState(true);
  const [loadingExtendStaysMessagesSent, setLoadingExtendStaysMessagesSent] = useState(true);
  const [loadingLeadsGenerated, setLoadingLeadsGenerated] = useState(true);
  const [loadingOrdersReceived, setLoadingOrdersReceived] = useState(true);
  const [loadingWorkOrdersCreated, setLoadingWorkOrdersCreated] = useState(true);

  // 16 stat values
  const [arrivingToday, setArrivingToday] = useState(0);
  const [currentlyStaying, setCurrentlyStaying] = useState(0);
  const [departingToday, setDepartingToday] = useState(0);
  const [last30Days, setLast30Days] = useState(0);
  const [homeGuides, setHomeGuides] = useState(0);
  const [areaGuides, setAreaGuides] = useState(0);
  const [scheduledMessages, setScheduledMessages] = useState(0);
  const [contacts, setContacts] = useState(0);
  const [textMessagesSent, setTextMessagesSent] = useState(0);
  const [guestGuideViews, setGuestGuideViews] = useState(0);
  const [ratingResponses, setRatingResponses] = useState(0);
  const [averageGuestRating, setAverageGuestRating] = useState(0);
  const [surveyResponses, setSurveyResponses] = useState(0);
  const [extendStaysMessagesSent, setExtendStaysMessagesSent] = useState(0);
  const [leadsGenerated, setLeadsGenerated] = useState(0);
  const [ordersReceived, setOrdersReceived] = useState(0);
  const [workOrdersCreated, setWorkOrdersCreated] = useState(0);

  const opsKPIs = {
    housekeeping:   { open: 6, inProgress: 4, completed: 28, overdue: 1 },
    inspections:    { open: 3, inProgress: 2, completed: 17, overdue: 0 },
    maintenance:    { open: 5, inProgress: 3, completed: 12, overdue: 2 },
    workOrders:     { open: 8, inProgress: 5, completed: 31, overdue: 1 },
  };

  const KpiRow = ({ label, value }) => (
    <div className="ops-kpi-row">
      <span>{label}</span>
      <strong>{Number(value).toLocaleString()}</strong>
    </div>
  );

  // Helper to get date range for filter
  const getDateRange = () => {
    const today = moment().format("YYYY-MM-DD");
    if (selectedFilter === "today") {
      return { startDate: today, endDate: today };
    } else if (selectedFilter === "yesterday") {
      const yest = moment().subtract(1, "days").format("YYYY-MM-DD");
      return { startDate: yest, endDate: yest };
    } else if (selectedFilter === "last7days") {
      const start = moment().subtract(6, "days").format("YYYY-MM-DD");
      return { startDate: start, endDate: today };
    } else if (selectedFilter === "last30days") {
      const start = moment().subtract(29, "days").format("YYYY-MM-DD");
      return { startDate: start, endDate: today };
    } else if (selectedFilter === "custom") {
      return { startDate, endDate };
    }
    return { startDate: today, endDate: today };
  };

  useEffect(() => {
    const { startDate, endDate } = getDateRange();
    setStartDate(startDate);
    setEndDate(endDate);
  }, [selectedFilter]);

  // Fetch first 8 overview stats only once on initial load
  useEffect(() => {
    dashboardCountsGuestsArrivingToday().then((res) => {
      setArrivingToday(res.guests_arriving_today ?? 0);
      setLoadingArrivingToday(false);
    });
    dashboardCountsGuestsCurrentlyStaying().then((res) => {
      setCurrentlyStaying(res.guests_currently_staying ?? 0);
      setLoadingCurrentlyStaying(false);
    });
    dashboardCountsGuestsDepartingToday().then((res) => {
      setDepartingToday(res.guests_departing_today ?? 0);
      setLoadingDepartingToday(false);
    });
    dashboardCountsGuestsLast30Days().then((res) => {
      setLast30Days(res.guests_last_30_days ?? 0);
      setLoadingLast30Days(false);
    });
    dashboardCountsHomeGuides().then((res) => {
      setHomeGuides(res.home_guides ?? 0);
      setLoadingHomeGuides(false);
    });
    dashboardCountsAreaGuides().then((res) => {
      setAreaGuides(res.area_guides ?? 0);
      setLoadingAreaGuides(false);
    });
    dashboardCountsScheduledMessages().then((res) => {
      setScheduledMessages(res.scheduled_messages ?? 0);
      setLoadingScheduledMessages(false);
    });
    dashboardCountsContacts().then((res) => {
      setContacts(res.contacts ?? 0);
      setLoadingContacts(false);
    });
  }, []);

  // Lazy fetch for last 8 stats (date range related) with debounce for custom date range
  useEffect(() => {
    let timeoutId;
    const fetchStats = async () => {
      setLoadingTextMessagesSent(true);
      setLoadingGuestGuideViews(true);
      setLoadingAverageGuestRating(true);
      setLoadingSurveyResponses(true);
      setLoadingExtendStaysMessagesSent(true);
      setLoadingLeadsGenerated(true);
      setLoadingOrdersReceived(true);
      setLoadingWorkOrdersCreated(true);

      dashboardCountsTextMessagesSent(startDate, endDate).then((res) => {
        setTextMessagesSent(res.text_messages_sent ?? 0);
        setLoadingTextMessagesSent(false);
      });
      dashboardCountsGuestGuideViews(startDate, endDate).then((res) => {
        setGuestGuideViews(res.guest_guide_views ?? 0);
        setLoadingGuestGuideViews(false);
      });
      dashboardCountsAvgGuestRating(startDate, endDate).then((res) => {
        setAverageGuestRating(Number(res.avg_guest_rating).toFixed(1) ?? 0);
        setRatingResponses(res.rating_responses ?? 0);
        setLoadingAverageGuestRating(false);
      });
      dashboardCountsSurveyResponses(startDate, endDate).then((res) => {
        setSurveyResponses(res.survey_responses ?? 0);
        setLoadingSurveyResponses(false);
      });
      dashboardCountsExtendStaysMessagesSent(startDate, endDate).then((res) => {
        setExtendStaysMessagesSent(res.extend_stays_messages_sent ?? 0);
        setLoadingExtendStaysMessagesSent(false);
      });
      dashboardCountsLeadsGenerated(startDate, endDate).then((res) => {
        setLeadsGenerated(res.leads_generated ?? 0);
        setLoadingLeadsGenerated(false);
      });
      dashboardCountsOrdersReceived(startDate, endDate).then((res) => {
        setOrdersReceived(res.orders_received ?? 0);
        setLoadingOrdersReceived(false);
      });
      dashboardCountsWorkOrdersCreated(startDate, endDate).then((res) => {
        setWorkOrdersCreated(res.work_orders_created ?? 0);
        setLoadingWorkOrdersCreated(false);
      });
    };
    timeoutId = setTimeout(fetchStats, 500);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [startDate, endDate]);

  const announcementsContext = useContext(AppContexts.AnnouncementsContext);
  const history = useHistory();

  useEffect(() => {
    document.title = "Welcome - RueBaRue";
    getRental();
    emailDNSCheck();
    // dashboardCounts();

    Intercom({
      app_id: 'f3c0pquv',
    });
  }, []);

  const property_error_cnt = useMemo(
    () => rentals.filter((rental) => rental.pms_id === "").length,
    [rentals],
  );

  const destination_error_cnt = useMemo(
    () => rentals.filter((rental) => !+rental.destination_id).length,
    [rentals],
  );

  const dismissAnnouncement = async (user_id, announcement_id) => {
    announcementsContext.dismissAnnouncement(user_id, announcement_id);
    const announcements = user.announcements.filter(
      (a) => a.id !== announcement_id,
    );
    setUser({ ...user, announcements: announcements });
  };

  const isAdmin = useMemo(() => {
    let value = `; ${document.cookie}`;
    let parts = value.split(`; admin_token=`);

    let cookie = null;
    if (parts.length === 2) {
      cookie = parts.pop().split(";").shift();
    }

    return !!cookie;
  }, []);

  return (
    <>
    <section id="dashboard-template">
      <div id="hms-page-title">
        <h1 className="uk-heading-small">
          Welcome, {`${user.user.first_name} ${user.user.last_name}`}
        </h1>
      </div>

      <div id="hms-main-body">
        <div className="uk-flex">
          <div className="uk-width-1-1">
            {!!isAdmin && dnsErrors?.length > 0 && (
              <div className="dns-errors">
                {dnsErrors.map((err, i) => (
                  <p key={`dns_${i}`}>{err}</p>
                ))}
              </div>
            )}

            {(!!property_error_cnt || !!destination_error_cnt) && (
              <div className="hms-notification-feed">
                <div className="uk-width-1-1">
                  {!!property_error_cnt && (
                    <div className="hms-notification-box">
                      <span>
                        <b>{property_error_cnt}</b> Home Guide(s) without a Property ID. 
                        Guests will not
                        receive automated messages unless resolved.{" "}
                      </span>
                      <a
                        href="https://intercom.help/ruebarueapp/en/articles/9792217-how-to-resolve-a-home-guide-without-a-property-id-alert"
                        target="blank"
                      >
                        Learn how to resolve it.
                      </a>
                    </div>
                  )}
                  {!!destination_error_cnt && (
                    <div className="hms-notification-box">
                      <span><b>{destination_error_cnt}</b> Home Guide(s) not linked to an Area Guide. </span>
                      <a
                        href="https://intercom.help/ruebarueapp/en/articles/9792214-how-to-resolve-a-home-guide-not-linked-to-an-area-guide-alert"
                        target="blank"
                      >
                        Learn how to resolve it.
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            <h2>Overview</h2>
            <div
              className="uk-margin-small-bottom uk-margin-none-top uk-grid-small uk-child-width-1-2 uk-child-width-1-4@m"
              uk-grid="true"
            >
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/guests")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico guests" uk-icon="calendar"></span>
                      Guests Arriving Today
                    </div>
                  </div>
                  <div className="hms-blue-number">
                    {loadingArrivingToday ? <span data-uk-spinner="ratio: 0.5;" /> : Number(arrivingToday).toLocaleString()}
                  </div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/guests")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico inhouse" uk-icon="users"></span>
                      Guests Currently Staying
                    </div>
                  </div>
                  <div className="hms-blue-number">
                    {loadingCurrentlyStaying ? <span data-uk-spinner="ratio: 0.5;" /> : Number(currentlyStaying).toLocaleString()}
                  </div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/guests")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico depart" uk-icon="calendar"></span>
                      Guests Departing Today
                    </div>
                  </div>
                  <div className="hms-blue-number">
                    {loadingDepartingToday ? <span data-uk-spinner="ratio: 0.5;" /> : Number(departingToday).toLocaleString()}
                  </div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/guests")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico guests" uk-icon="users"></span>
                      Guests Last 30 Days
                    </div>
                  </div>
                  <div className="hms-blue-number">
                    {loadingLast30Days ? <span data-uk-spinner="ratio: 0.5;" /> : Number(last30Days).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
            <div className="uk-grid-small uk-child-width-1-2 uk-child-width-1-4@m uk-margin-none-top" uk-grid="true">
              {FEATURES.HOUSEKEEPING_ENABLED && (
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/housekeeping")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico" uk-icon="paint-bucket"></span>
                      Cleaning
                    </div>
                    <span className="rbr-tag">This Week</span>
                  </div>
                  <KpiRow label="Open"         value={opsKPIs.housekeeping.open} />
                  <KpiRow label="In Progress"  value={opsKPIs.housekeeping.inProgress} />
                  <KpiRow label="Completed"    value={opsKPIs.housekeeping.completed} />
                  <KpiRow label="Overdue"      value={opsKPIs.housekeeping.overdue} />
                </div>
              </div>
              )}

              {/* Inspections */}
              {FEATURES.INSPECTIONS_ENABLED && (
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/inspections")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico" uk-icon="search"></span>
                      Inspections
                    </div>
                    <span className="rbr-tag">This Week</span>
                  </div>
                  <KpiRow label="Open"         value={opsKPIs.inspections.open} />
                  <KpiRow label="In Progress"  value={opsKPIs.inspections.inProgress} />
                  <KpiRow label="Completed"    value={opsKPIs.inspections.completed} />
                  <KpiRow label="Overdue"      value={opsKPIs.inspections.overdue} />
                </div>
              </div>
              )}

              {/* Maintenance */}
              {FEATURES.MAINTENANCE_ENABLED && (
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/maintenance")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico" uk-icon="file-text"></span>
                      Maintenance
                    </div>
                    <span className="rbr-tag">This Week</span>
                  </div>
                  <KpiRow label="Open"         value={opsKPIs.maintenance.open} />
                  <KpiRow label="In Progress"  value={opsKPIs.maintenance.inProgress} />
                  <KpiRow label="Completed"    value={opsKPIs.maintenance.completed} />
                  <KpiRow label="Overdue"      value={opsKPIs.maintenance.overdue} />
                </div>
              </div>
              )}

              {/* Work Orders */}
              {FEATURES.WORK_ORDERS_MAIN_DASHBOARD_ENABLED && (
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/work-orders")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico" uk-icon="file-text"></span>
                      Work Orders
                    </div>
                    <span className="rbr-tag">This Week</span>
                  </div>
                  <KpiRow label="Open"         value={opsKPIs.workOrders.open} />
                  <KpiRow label="In Progress"  value={opsKPIs.workOrders.inProgress} />
                  <KpiRow label="Completed"    value={opsKPIs.workOrders.completed} />
                  <KpiRow label="Overdue"      value={opsKPIs.workOrders.overdue} />
                </div>
              </div>
              )}
            </div>

            <div
              className="uk-margin-small-bottom uk-margin-small-top uk-grid-small uk-child-width-1-2 uk-child-width-1-4@m"
              uk-grid="true"
              >
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/properties")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico guides" uk-icon="home"></span>
                      Home Guides
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingHomeGuides ? <span data-uk-spinner="ratio: 0.5;" /> : Number(homeGuides).toLocaleString()}</div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/areas")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico area" uk-icon="location"></span>
                      Area Guides
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingAreaGuides ? <span data-uk-spinner="ratio: 0.5;" /> : Number(areaGuides).toLocaleString()}</div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/scheduler")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico sched" uk-icon="clock"></span>
                      Scheduled Messages
                    </div>
                    <span className="rbr-tag">Active</span>
                  </div>
                  <div className="hms-blue-number">{loadingScheduledMessages ? <span data-uk-spinner="ratio: 0.5;" /> : Number(scheduledMessages).toLocaleString()}</div>
                </div>
              </div>

              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/contacts")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico contacts" uk-icon="user"></span>
                      Contacts
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingContacts ? <span data-uk-spinner="ratio: 0.5;" /> : Number(contacts).toLocaleString()}</div>
                </div>
              </div>          
            </div>

            <h2 className="uk-margin-large-top">Stats</h2>
            <div
              uk-grid="true"
              >
              <div className="uk-button-group uk-margin-right">
                <button
                  className={`uk-button dashboard-tab-button ${selectedFilter === 'today' ? 'uk-button-primary' : 'uk-button-default'}`}
                  onClick={() => setSelectedFilter('today')}
                >
                  Today
                </button>
                <button
                  className={`uk-button dashboard-tab-button ${selectedFilter === 'yesterday' ? 'uk-button-primary' : 'uk-button-default'}`}
                  onClick={() => setSelectedFilter('yesterday')}
                >
                  Yesterday
                </button>
                <button
                  className={`uk-button dashboard-tab-button ${selectedFilter === 'last7days' ? 'uk-button-primary' : 'uk-button-default'}`}
                  onClick={() => setSelectedFilter('last7days')}
                >
                  Last 7 Days
                </button>
                <button
                  className={`uk-button dashboard-tab-button ${selectedFilter === 'last30days' ? 'uk-button-primary' : 'uk-button-default'}`}
                  onClick={() => setSelectedFilter('last30days')}
                >
                  Last 30 Days
                </button>
                <button
                  className={`uk-button dashboard-tab-button ${selectedFilter === 'custom' ? 'uk-button-primary' : 'uk-button-default'}`}
                  onClick={() => setSelectedFilter('custom')}
                >
                  Custom
                </button>
              </div>
              {selectedFilter === 'custom' && (
                <div className="uk-width-auto">
                  <div className="uk-grid-small uk-margin-none-top" uk-grid="true">
                    <div className="uk-width-1-2">
                      <input
                        className="uk-input"
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          validateDates();
                        }}
                      />
                    </div>
                    <div className="uk-width-1-2">
                      <input
                        className="uk-input"
                        type="date"
                        value={endDate}
                        onChange={(e) => {
                          setEndDate(e.target.value);
                          validateDates();
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}              
            </div>

            <div
              className="uk-margin-small-bottom uk-margin-small-top uk-grid-small uk-child-width-1-2 uk-child-width-1-4@m"
              uk-grid="true"
              >
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/reports/message-log")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico sched" uk-icon="commenting"></span>
                      Text Messages Sent
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingTextMessagesSent ? <span data-uk-spinner="ratio: 0.5;" /> : Number(textMessagesSent).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/reports/guestbook-views")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico depart" uk-icon="plus"></span>
                      Guest Guide Views
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingGuestGuideViews ? <span data-uk-spinner="ratio: 0.5;" /> : Number(guestGuideViews).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico area" uk-icon="star"></span>
                      Average Guest Rating
                    </div>
                    <span className="rbr-tag">{Number(ratingResponses).toLocaleString()} Ratings</span>
                  </div>
                  <div className="hms-blue-number">{loadingAverageGuestRating ? <span data-uk-spinner="ratio: 0.5;" /> : Number(averageGuestRating).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/surveys")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico leads" uk-icon="list"></span>
                      Survey Responses
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingSurveyResponses ? <span data-uk-spinner="ratio: 0.5;" /> : Number(surveyResponses).toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div
              className="uk-margin-small-bottom uk-margin-none-top uk-grid-small uk-child-width-1-2 uk-child-width-1-4@m"
              uk-grid="true"
              >
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/reports/extend-stay")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico leads" uk-icon="calendar"></span>
                      Extend Stays Messages Sent
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingExtendStaysMessagesSent ? <span data-uk-spinner="ratio: 0.5;" /> : Number(extendStaysMessagesSent).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/contacts")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico area" uk-icon="check"></span>
                      Leads Generated
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingLeadsGenerated ? <span data-uk-spinner="ratio: 0.5;" /> : Number(leadsGenerated).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/orders")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico leads" uk-icon="cart"></span>
                      Orders Received
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingOrdersReceived ? <span data-uk-spinner="ratio: 0.5;" /> : Number(ordersReceived).toLocaleString()}</div>
                </div>
              </div>
              <div>
                <div className="rbr-card uk-margin-small-right" onClick={() => history.push("/work-orders")}>
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico leads" uk-icon="cart"></span>
                      Work Orders Created
                    </div>
                  </div>
                  <div className="hms-blue-number">{loadingWorkOrdersCreated ? <span data-uk-spinner="ratio: 0.5;" /> : Number(workOrdersCreated).toLocaleString()}</div>
                </div>
              </div>
            </div>            

            {/*

            <div className="uk-grid-small rbr-kpis" uk-grid="true">
              <div className="uk-width-1-2 uk-width-1-4@m uk-margin-small-right">
                <div className="rbr-card">
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico contacts" uk-icon="mail"></span>
                      Guest Rating
                    </div>
                    <span className="rbr-tag">Last 30 days</span>
                  </div>
                  <div className="rbr-value">{counts.messages_month ?? 0}</div>
                  <div className="rbr-meta">
                    Avg response {counts.avg_response_mins ?? 0}m
                  </div>
                </div>
              </div>

              <div className="uk-width-1-2 uk-width-1-4@m uk-margin-small-right">
                <div className="rbr-card">
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico leads" uk-icon="plus"></span>
                      Leads generated
                    </div>
                    <span className="rbr-tag">Last 30 days</span>
                  </div>
                  <div className="rbr-value">{counts.leads_month ?? 0}</div>
                  <div className="rbr-meta rbr-up">Trending up</div>
                </div>
              </div>

              <div className="uk-width-1-2 uk-width-1-4@m uk-margin-small-right">
                <div className="rbr-card">
                  <div className="rbr-card-head">
                    <div className="rbr-card-title">
                      <span className="rbr-ico sched" uk-icon="clock"></span>
                      Avg. response time
                    </div>
                    <span className="rbr-tag">Minutes</span>
                  </div>
                  <div className="rbr-value">{counts.avg_response_mins ?? 0}</div>
                  <div className="rbr-meta">
                    95th pct: {counts.p95_response_mins ?? 0}m
                  </div>
                </div>
              </div>
            </div>
            */}

            {user.announcements.map((announcement) => (
              <div
                className="rbr-card rbr-announcement uk-margin-top"
                key={`ann_${announcement.id}`}
              >
                {/* Close button */}
                <button
                  className="rbr-close-btn"
                  type="button"
                  onClick={() =>
                    dismissAnnouncement(user.user_id, announcement.id)
                  }
                >
                  ×
                </button>

                <div className="rbr-badge">
                  <span className="rbr-ico" uk-icon="info"></span>
                  Tip</div>

                <div className="uk-margin-small-top uk-text-bold">
                  {announcement.title}
                </div>

                <div className="rbr-meta">{announcement.description}</div>

                {announcement.link && (
                  <div className="uk-margin-small-top">
                    <a
                      href={announcement.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rbr-link"
                    >
                      Learn More <span uk-icon="chevron-right"></span>
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="uk-flex uk-flex-wrap uk-margin rbr-pills">
            {scheduledMessages > 0 ? (
              <span className="rbr-pill ok">
                <span uk-icon="bolt"></span> Automation On
              </span>
            ) : (
              <span className="rbr-pill warn">
                <span uk-icon="ban"></span> Automation Off
              </span>
            )}
            {dnsErrors?.length > 0 ? (
            <span className="rbr-pill warn">
              <span uk-icon="warning"></span> {dnsErrors.length} DNS Issue(s). Contact support.
            </span>
          ) : (
            <span className="rbr-pill ok">
              <span uk-icon="check"></span> DNS OK
            </span>
          )}
          {/* 
          <span className="rbr-pill ok">
            <span uk-icon="happy"></span> PMS Connected
          </span>
            */}
        </div>
      </div>
    </section>    
    </>
  );
};

export default Dashboard;
